import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  Building2,
  Download,
  FolderGit2,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { listOrganizations, listProjects } from "@/lib/console-api";
import { useAuthStore } from "@/stores/auth-store";

const ROLE_LABEL: Record<string, string> = {
  OWNER: "소유자",
  ADMIN: "관리자",
  MEMBER: "멤버",
  VIEWER: "뷰어",
};

const linkBtn = cn(
  "inline-flex h-10 items-center justify-center gap-2 rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90",
);

const linkBtnOutline = cn(
  "inline-flex h-10 items-center justify-center gap-2 rounded-md border border-input bg-background px-5 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground",
);

export function DashboardPage(): JSX.Element {
  const account = useAuthStore((s) => s.account);

  const orgsQuery = useQuery({
    queryKey: ["organizations"],
    queryFn: listOrganizations,
  });
  const projectsQuery = useQuery({
    queryKey: ["projects", { organizationId: undefined }],
    queryFn: () => listProjects(),
  });

  const orgs = orgsQuery.data ?? [];
  const projectsTotal = projectsQuery.data?.total ?? 0;
  const ownerCount = orgs.filter(
    (o) => o.role === "OWNER" || o.role === "ADMIN",
  ).length;

  return (
    <div className="space-y-8 p-8">
      <header>
        <p className="text-sm text-muted-foreground">대시보드</p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight">
          {account?.name?.trim()
            ? `${account.name}님, 다시 만나요`
            : "다시 만나요"}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          오늘도 한 PR 더 가까이 가 봅시다.
        </p>
      </header>

      {/* 요약 카드 */}
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <SummaryCard
          icon={Building2}
          label="가입한 조직"
          value={
            orgsQuery.isLoading ? "—" : `${orgs.length}곳`
          }
          hint={
            ownerCount > 0
              ? `소유자/관리자 ${ownerCount}곳`
              : "초대받은 조직만"
          }
        />
        <SummaryCard
          icon={FolderGit2}
          label="프로젝트"
          value={
            projectsQuery.isLoading ? "—" : `${projectsTotal}개`
          }
          hint="조직 전체 합계"
        />
        <SummaryCard
          icon={Sparkles}
          label="시작 권장"
          value={
            account?.name?.trim() ? "데스크톱 앱" : "조직 선택"
          }
          hint="아래 카드에서 다음 행동을"
        />
      </section>

      {/* 다음 행동 — 인증 직후 사용자가 자연스럽게 따라갈 수 있도록 */}
      <section className="rounded-lg border border-border bg-card p-6">
        <h2 className="text-lg font-semibold">다음 행동</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          처음이라면 데스크톱 앱부터, 익숙해지셨다면 조직과 프로젝트를 살펴보세요.
        </p>
        <div className="mt-5 flex flex-col gap-3 sm:flex-row">
          <Link to="/download" className={linkBtn}>
            <Download className="h-4 w-4" /> 데스크톱 앱 받기
          </Link>
          <Link to="/app/organizations" className={linkBtnOutline}>
            <Building2 className="h-4 w-4" /> 조직 둘러보기
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* 조직 미리보기 (최대 3개) */}
      <section>
        <div className="mb-3 flex items-end justify-between">
          <h2 className="text-lg font-semibold">조직</h2>
          <Link
            to="/app/organizations"
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            전체 보기 →
          </Link>
        </div>
        {orgsQuery.isLoading && (
          <p className="text-sm text-muted-foreground">불러오는 중…</p>
        )}
        {!orgsQuery.isLoading && orgs.length === 0 && (
          <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            소속된 조직이 아직 없습니다.
          </p>
        )}
        {!orgsQuery.isLoading && orgs.length > 0 && (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {orgs.slice(0, 3).map((org) => (
              <li
                key={org.id}
                className="rounded-lg border border-border bg-card p-4"
              >
                <div className="flex items-center justify-between">
                  <p className="font-medium">{org.name}</p>
                  <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    {ROLE_LABEL[org.role] ?? org.role}
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {org.plan} · {org.slug}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function SummaryCard({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  hint?: string;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" aria-hidden />
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold tracking-tight">{value}</div>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
